import z from "zod";

import { UnloadingDirectionDTOTariffRoad } from "@/common/api";
import { locale } from "@/common/locale";
import { preprocessNumber } from "@/common/model";

const MIN_INT = 0 as const;
const MAX_INT = 9999 as const;
const MAX_FLOAT = 999999.99 as const;

export const wagonSchema = preprocessNumber(
  z.coerce
    .number({ invalid_type_error: locale.notValid })
    .int({ message: locale.notInteger })
    .min(MIN_INT, `${locale.minValue} ${MIN_INT}`)
    .max(MAX_INT, `${locale.maxValue} ${MAX_INT}`),
);

export const requiredTariffSchema = preprocessNumber(
  z.coerce
    .number({ invalid_type_error: locale.notValid })
    .min(MIN_INT, `${locale.minValue} ${MIN_INT}`)
    .max(MAX_FLOAT, `${locale.maxValue} ${MAX_FLOAT}`),
);

export const optionalTariffSchema = preprocessNumber(
  z.coerce
    .number({ invalid_type_error: locale.notValid })
    .min(MIN_INT, `${locale.minValue} ${MIN_INT}`)
    .max(MAX_FLOAT, `${locale.maxValue} ${MAX_FLOAT}`)
    .optional(),
);

/**
 * Проверяет, является ли тариф VAT тарифом (WITH_20_VAT, WITH_0_VAT, WITH_OUT_VAT)
 */
const isVatTariff = (tariffType: UnloadingDirectionDTOTariffRoad): boolean => {
  return (
    tariffType === UnloadingDirectionDTOTariffRoad.WITH_20_VAT ||
    tariffType === UnloadingDirectionDTOTariffRoad.WITH_0_VAT ||
    tariffType === UnloadingDirectionDTOTariffRoad.WITH_OUT_VAT
  );
};

export const validateTariffForVatType = (
  wagonTariff: number,
  tariffRoad: UnloadingDirectionDTOTariffRoad,
): boolean => {
  // Если это не VAT тариф, валидация проходит (дробные числа допустимы)
  if (!isVatTariff(tariffRoad)) {
    return true;
  }

  // Для VAT тарифов проверяем, что wagonTariff является целым числом
  return Number.isInteger(wagonTariff);
};

export const ownerSummingUpFormSchema = z.object({
  participants: z.array(
    z.object({
      wagons: wagonSchema.nullable().optional(),
    }),
  ),
});
export type OwnerSummingUpFormValues = z.infer<typeof ownerSummingUpFormSchema>;

export const ownerProposalFormSchema = z
  .object({
    proposals: z.record(z.string(), z.any()),
    tariffRoad: z.record(
      z.string(),
      z.nativeEnum(UnloadingDirectionDTOTariffRoad).optional(),
    ),
  })
  .superRefine((data, ctx) => {
    // Валидируем каждое значение proposals
    Object.entries(data.proposals || {}).forEach(([key, value]) => {
      // Сначала валидируем базовую схему (число, мин/макс)
      const baseValidation = optionalTariffSchema.safeParse(value);
      if (!baseValidation.success) {
        baseValidation.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ["proposals", key],
          });
        });
        return;
      }

      const wagonTariff = baseValidation.data;

      // Пропускаем undefined, так как поле опциональное
      if (wagonTariff === undefined) {
        return;
      }

      const tariffRoad = data.tariffRoad?.[key];

      if (!tariffRoad) {
        return; // Если tariffRoad не указан, пропускаем валидацию VAT
      }

      const isValid = validateTariffForVatType(wagonTariff, tariffRoad);

      if (!isValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: locale.notInteger,
          path: ["proposals", key],
        });
      }
    });
  });
export type OwnerProposalFormValues = z.infer<typeof ownerProposalFormSchema>;

export const sobProposalFormSchema = z
  .object({
    proposals: z.record(z.string(), z.any()),
    tariffRoad: z.record(
      z.string(),
      z.nativeEnum(UnloadingDirectionDTOTariffRoad).optional(),
    ),
  })
  .superRefine((data, ctx) => {    
    // Валидируем каждое значение proposals
    Object.entries(data.proposals || {}).forEach(([key, value]) => {
      // Сначала валидируем базовую схему (число, мин/макс)
      const baseValidation = requiredTariffSchema.safeParse(value);
      if (!baseValidation.success) {
        baseValidation.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ["proposals", key],
          });
        });
        return;
      }
      
      const wagonTariff = baseValidation.data;
      
      const tariffRoad = data.tariffRoad?.[key];
      
      if (!tariffRoad) {
        return; // Если tariffRoad не указан, пропускаем валидацию VAT
      }
      
      const isValid = validateTariffForVatType(wagonTariff as number, tariffRoad);
      
      if (!isValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: locale.notInteger,
          path: ["proposals", key],
        });
      }
    });
  });
export type SobProposalFormValues = z.infer<typeof sobProposalFormSchema>;

export const sobOfferFormSchema = z.object({
  tariffs: z.array(
    z
      .object({
        wagonTariff: requiredTariffSchema,
        indexNumber: z.number(),
        tariffRoad: z.nativeEnum(UnloadingDirectionDTOTariffRoad),
      })
      .refine(
        (data) => {
          return validateTariffForVatType(data.wagonTariff as number, data.tariffRoad);
        },
        {
          message: locale.notInteger,
          path: ["wagonTariff"],
        },
      ),
  ),
  wagon: wagonSchema,
});
export type SobOfferFormValues = z.infer<typeof sobOfferFormSchema>;
